import { reflect } from '@effector/reflect';
import { useUnit } from 'effector-react';
import { CircleSlash2 } from 'lucide-react';
import { not } from 'patronum';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { viewerModel } from 'entities/viewer';
import { translated } from 'shared/lib';
import { ConfirmModalProps, createConfirmModal } from 'shared/ui';
import { BlockCashdeskModel } from '../../model';

export const createBlockCashdeskModal = (model: BlockCashdeskModel) => {
	const ConfirmModal = createConfirmModal(model);

	const BlockCashdeskModal = forwardRef<HTMLDivElement, ConfirmModalProps>(({ ...props }, ref) => {
		const { t } = useTranslation();

		const [cashdesk] = useUnit([model.$modalData]);

		return (
			<ConfirmModal
				ref={ref}
				title={t('block-cashdesk.title')}
				cancelButtonProps={{
					children: t('block-cashdesk.cancel'),
				}}
				submitButtonProps={{
					variant: 'outline',
					children: t('block-cashdesk.block'),
				}}
				{...props}
			>
				{t('block-cashdesk.description', { name: cashdesk?.name })}
			</ConfirmModal>
		);
	});

	const BlockCashdeskModalButton = reflect({
		view: ConfirmModal.Button,
		bind: {
			'data-test-id': 'block-cashdesk-button',
			variant: 'outline',
			disabled: not(viewerModel.$canWriteCashdesks),
			leftSection: <CircleSlash2 />,
			children: translated('block-cashdesk.block'),
		},
	});

	const BlockCashdeskModalActionIcon = reflect({
		view: ConfirmModal.ActionIcon,
		bind: {
			'data-test-id': 'block-cashdesk-action-icon',
			variant: 'ghost-secondary',
			disabled: not(viewerModel.$canWriteCashdesks),
			children: <CircleSlash2 />,
		},
	});

	return Object.assign(BlockCashdeskModal, {
		Button: BlockCashdeskModalButton,
		ActionIcon: BlockCashdeskModalActionIcon,
	});
};
